import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { X, Copy, Check, Heart, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const UPI_ID = "najadhav@okhdfcbank";
const PAYEE = "My Kind Guru Dakshina";
const PRESETS = [1, 11, 21, 51, 101, 251, 501];

// Open from anywhere: window.dispatchEvent(new CustomEvent("open-gurudakshina"))
export function GuruDakshina() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(101);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onOpen = () => { setAmount(101); setOpen(true); };
    window.addEventListener("open-gurudakshina", onOpen);
    return () => window.removeEventListener("open-gurudakshina", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const amt = Math.max(1, Math.floor(Number(amount) || 0));
  const upiLink = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(PAYEE)}&am=${amt}&cu=INR&tn=${encodeURIComponent("Guru Dakshina 🙏")}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(UPI_ID); setCopied(true); toast.success("UPI ID copied"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy — please copy it manually."); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] grid place-items-center p-4 overflow-y-auto"
          style={{ backgroundColor: "rgba(0,0,0,0.65)" }}
          data-testid="gurudakshina-modal"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-rud glass p-6 relative my-8"
          >
            <button data-testid="gurudakshina-close" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-sec hover:text-[var(--rud-text)]"><X size={18} /></button>

            <div className="flex items-center gap-2 text-brand text-xs uppercase tracking-widest"><Heart size={14} /> Guru Dakshina</div>
            <h2 className="font-serif text-2xl mt-2">Offer Guru Dakshina 🙏</h2>
            <p className="text-sec text-sm mt-1.5 leading-relaxed">Your offering keeps <b className="text-[var(--rud-text)]">learning and competitive-exam prep free for all</b> on My Kind. Give from the heart — every amount is a blessing. <span className="text-brand">Sarvesham Shantir Bhavatu.</span></p>

            <div className="flex flex-wrap gap-2 mt-5" data-testid="gurudakshina-presets">
              {PRESETS.map((p) => (
                <button key={p} data-testid={`gd-amt-${p}`} onClick={() => setAmount(p)}
                  className={`h-9 px-3 rounded-full text-sm transition-colors ${amt === p ? "bg-brand text-black font-medium" : "bg-white/5 border border-white/10 text-sec hover:text-[var(--rud-text)]"}`}>
                  ₹{p}
                </button>
              ))}
            </div>

            <div className="mt-3">
              <label className="text-xs text-sec">Or enter a custom amount (₹)</label>
              <input data-testid="gd-custom-amount" type="number" min={1} value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-1" />
            </div>

            <div className="mt-5 flex flex-col items-center">
              <div className="bg-white p-3 rounded-xl" data-testid="gurudakshina-qr">
                <QRCodeSVG value={upiLink} size={188} level="M" includeMargin={false} />
              </div>
              <div className="text-xs text-sec mt-2">Scan with any UPI app to give <b className="text-[var(--rud-text)]">₹{amt}</b></div>
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-lg border border-rud bg-white/5 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-sec">UPI ID · HDFC Bank</div>
                <div className="text-sm truncate" data-testid="gurudakshina-upi">{UPI_ID}</div>
              </div>
              <button data-testid="gurudakshina-copy" onClick={copy} className="btn-ghost rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5">
                {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
              </button>
            </div>

            <a data-testid="gurudakshina-pay" href={upiLink} className="btn-primary w-full h-11 rounded-full mt-4 flex items-center justify-center gap-2 text-sm">
              <Heart size={15} /> Pay ₹{amt} via UPI app <ExternalLink size={14} />
            </a>
            <p className="text-[11px] text-sec text-center mt-2">On mobile this opens GPay / PhonePe / Paytm etc. On desktop, scan the QR above.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default GuruDakshina;
